module Meevo.Plugins.Register {
    "use strict";
    interface IPaymentPlanTemplateResponse {
        data: IPaymentPlanTemplate
    }

    interface IPaymentPlanTemplate {
        amountPerPayment: number;
        finalPaymentAmount: number;
        maximumPaymentPlanAmount: number;
        maximumNumberOfPayments: number;
        maximumFirstPaymentDate: Date;
        minimumFirstPaymentDate: Date;
    }

    interface IPaymentPlanRefund extends Model.PaymentPlan {
        saleLines: Model.SaleLine[];
        header: string;
        refundAmount: number;
        planReductionAmount: number;
        refundCredit: number;
        previousReductionAmount: number;
        isInitial: boolean;
        tax: number;
        prePaidTip: number;
    }

    export class PaymentPlanPaymentsSection extends Meevo.Plugins.Register.RegisterPaymentSectionBase {
        constructor(registerContext: Meevo.Plugins.Register.RegisterContext) {
            super(RegisterSectionTypeEnum.PaymentPlan, registerContext, "paymentPlanPayments", Register.RegisterSectionCatTypeEnum.Payment);

            this.meevoCurrency = this.registerContext.$filter("meevoCurrency");
            this.meevoDate = this.registerContext.$filter("meevoDateFilter");
        }

        payment: PaymentPlanSalePayment;
        paymentSchedule: string = "";
        meevoCurrency;
        meevoDate;
        minDownPayment: number;
        planTemplate: IPaymentPlanTemplate;
        refundPaymentPlans: IPaymentPlanRefund[];
        sale: Model.Sale;
        previousDownPayment: number;
        numberOfPayments?: number;
        firstPaymentDate?: Date;
        paymentPlanSaleLineTotal: number;
        isEditing: boolean;
        calculateReduction: boolean = true;
        isSaleLineSelectionMode: boolean = false;
        hasHsaFsaMerchantAccount: boolean;
        hostedPageIntent = Meevo.Model.HostedPageIntent.Installment;
        isHostedPageAutomaticallySwitch: boolean;

        _paymentPlan: Model.PaymentPlan;
        get paymentPlan() { return this._paymentPlan; }
        set paymentPlan(model) {
            this._paymentPlan = model;
            this.numberOfPayments = model.numberOfPayments;
            this.firstPaymentDate = model.firstPaymentDate;
        }

        clearCache() {
            this.registerContext.paymentPlanService.paymentPlanMap = {};
            this.registerContext.paymentPlanService.refundPaymentPlanIds = [];
            this.sale = undefined;
            this.refundPaymentPlans = [];
        }

        reset() {
            if (this.registerContext.isRefundMode) {
                this.sale = undefined;
                this.buildRefundLines(this.registerContext.sale, this.calculateReduction).then(() => {
                    // automatically add payment plan reduction line to ticket in case user doesn't click apply
                    this.applyRefundItems(this.registerContext.sale, true).then(() => {
                        this.sale = jQuery.extend(true, {}, this.registerContext.sale);
                        this.sale.isTemp = true;
                        // need to rebuild again with the new sale model that contains reduction line
                        this.buildRefundLines(this.sale, this.calculateReduction).then(() => {
                            this.calculateReduction = true;
                        })
                    })
                })
                return;
            }

            this.isSaleLineSelectionMode = false;
            this.isEditing = false;
            this.payment = this.createPaymentPlanPayment();
            // Template call doesn't allow 0 for amount so set to lowest amount possible for culture
            this.payment.amount = this.registerContext.getSmallestAmountForCurrency();
            this.paymentPlan = {
                paymentAccount: {
                    paymentAccountType: Model.PaymentAccountTypeEnum.Card,
                    merchantAccountId: this.registerContext.selectedDrawer.merchantAccountId ?
                        this.registerContext.selectedDrawer.merchantAccountId.toString() : undefined,
                    directManualEntry: this.registerContext.getMerchantAccountDirectManualEntry()
                },
                downPayment: 0,
                numberOfPayments: 1
            }

            let actualClients = _(this.registerContext.allUniqueClients).where({ clientType: Model.ClientTypeEnum.Client });
            if (actualClients.length === 1) {
                this.paymentPlan.client = actualClients[0].client;
            }

            // Need to get template here for the 1st time so can get setting for max plan amount which
            // determines min down payment amount which is needed for all subsequent calls to gettemplate
            this.getTemplate().then(() => {
                this.update(this.payment, this.paymentPlan);
                this.firstPaymentDate = this.planTemplate.minimumFirstPaymentDate;
            });
            this.setHasHsaFsaMerchantAccounts();
            this.isHostedPageAutomaticallySwitch = this.registerContext.getIsHostedPageAutomaticallySwitch();
        }

        private updateRefundLinePaymentPlanEntityVersion(saleLines: Model.SaleLine[]) {
            for (let paymentPlanSaleLine of _(saleLines).filter((saleLine) => {
                return saleLine.isRefund && !_(saleLine.paymentPlanId).isEmpty()
            })) {
                paymentPlanSaleLine.item.paymentPlanEntityVersion = this.registerContext.paymentPlanService.paymentPlanMap[paymentPlanSaleLine.paymentPlanId].entityVersion;
            }
        }

        getPaymentPlansForRefund(paymentPlanIds: string[], sale: Model.Sale) {
            let getPromises: angular.IPromise<Model.PaymentPlan>[] = [];
            for (let paymentPlanId of paymentPlanIds) {
                if (!this.registerContext.paymentPlanService.paymentPlanMap[paymentPlanId]) {
                    this.registerContext.paymentPlanService.paymentPlanMap[paymentPlanId] = {};
                    getPromises.push(this.registerContext.paymentPlanService.getPaymentPlan(
                        paymentPlanId, this.registerContext).
                        then((plan: Model.PaymentPlan) => {
                            this.registerContext.paymentPlanService.paymentPlanMap[paymentPlanId] = plan;
                            return plan;
                        }));
                }
                else {
                    getPromises.push(this.registerContext.$q.resolve<Model.PaymentPlan>(this.registerContext.paymentPlanService.paymentPlanMap[paymentPlanId]));
                }
            }
            return this.registerContext.$q.all(getPromises).then((paymentPlans) => {
                this.updateRefundLinePaymentPlanEntityVersion(sale.saleLines);
                return paymentPlans;
            })
        }

        buildRefundLines(sale: Model.Sale, calculateReduction: boolean = false) {
            // retrieve any needed payment plans and remove any that are no longer needed
            let paymentPlanIds = _(sale.saleLines).chain().filter(saleLine => {
                return saleLine.paymentPlanId && saleLine.isRefund &&
                    this.registerContext.paymentPlanService.paymentPlanHasRemainingBalance(saleLine.paymentPlanId);
            }).pluck("paymentPlanId").uniq().value();

            return this.getPaymentPlansForRefund(paymentPlanIds, sale).then(() => {
                this.refundPaymentPlans = [];
                for (const paymentPlanId of paymentPlanIds) {
                    let refundPaymentPlan = <IPaymentPlanRefund>this.registerContext.paymentPlanService.paymentPlanMap[paymentPlanId];
                    refundPaymentPlan.isInitial = false;
                    refundPaymentPlan.refundAmount = 0;
                    if (!refundPaymentPlan.header) {
                        refundPaymentPlan.isInitial = true;
                        refundPaymentPlan.header = this.registerContext.$translate.instant("HEADER_PAYMENT_PLAN_REFUND", {
                            amountPaid: this.meevoCurrency(refundPaymentPlan.maxInstantRefundAmount),
                            amountRemaining: this.meevoCurrency(refundPaymentPlan.remainingBalance)
                        });
                    }
                    refundPaymentPlan.saleLines = _(sale.saleLines).where({ paymentPlanId: refundPaymentPlan.id });

                    this.refundPaymentPlans.push(refundPaymentPlan);
                }
                for (let plan of this.refundPaymentPlans) {
                    this.updateRefundTotals(plan, plan.isInitial || calculateReduction)
                }
            })
        }

        updateRefundTotals(plan: IPaymentPlanRefund, calculateReduction: boolean) {
            plan.refundAmount = plan.tax = plan.prePaidTip = 0;
            for (let saleLine of plan.saleLines) {
                let tax = saleLine.priceIncludesTax ? 0 : -saleLine.tax;
                plan.refundAmount = this.registerContext.addNormalizedNumbers(plan.refundAmount, (saleLine.newFinalItemPrice * saleLine.quantity) + -saleLine.prepaidTip + tax);
                plan.tax = this.registerContext.addNormalizedNumbers(plan.tax, tax);
                plan.prePaidTip = this.registerContext.addNormalizedNumbers(plan.prePaidTip, -saleLine.prepaidTip);
            }

            if (calculateReduction) {
                plan.planReductionAmount = Math.min(plan.refundAmount, plan.remainingBalance);
            }
            // if refundAmount changes (price of item is reduced, make sure reduction amount is valid)
            else if (plan.planReductionAmount > plan.refundAmount) {
                plan.planReductionAmount = plan.refundAmount;
            }
            plan.previousReductionAmount = plan.planReductionAmount;
            plan.refundCredit = this.registerContext.subtractNormalizedNumbers(plan.refundAmount, plan.planReductionAmount);
            if (plan.refundCredit > plan.maxInstantRefundAmount) {
                this.updateRefundTotals(plan, true);
            }

            this.refundTotal = _(this.refundPaymentPlans).reduce((sum, plan) => {
                return this.registerContext.addNormalizedNumbers(plan.refundAmount, sum);
            }, 0);
        }

        canApplyRefund() {
            return !this.registerContext.registerTicketService.recalculateSaleInProgress;
        }

        applyRefundItems(sale?: Model.Sale, isReset: boolean = false, recalc: boolean = true): angular.IPromise<void> {
            sale = sale || this.sale;
            sale.isTemp = false;
            this.refundPaymentPlans.forEach((plan: IPaymentPlanRefund) => {
                let saleLine = _(sale.saleLines).find(saleLine => {
                    return saleLine.saleType === Model.SalesTypeEnum.PaymentPlanReduction &&
                        saleLine.item.id === plan.id;
                });
                if (!saleLine) {
                    saleLine = {
                        item: new PaymentPlanReductionSaleLineItem({
                            id: plan.id,
                            displayName: this.registerContext.$translate.instant("LABEL_PAYMENT_PLAN_REDUCTION_SALE_LINE_DISPLAY_NAME",
                                { saleTransactionId: plan.saleLines[0].refundTransactionId })
                        }),
                        quantity: 1,
                        client: plan.client,
                        employeeId: this.registerContext.HouseEmployee.id,
                        employee: this.registerContext.HouseEmployee,
                        clientId: plan.client.id,
                        clientType: Model.ClientTypeEnum.Client,
                        saleType: Model.SalesTypeEnum.PaymentPlanReduction
                    };
                    sale.saleLines.push(saleLine);
                }
                saleLine.baseItemPrice = saleLine.finalItemPrice = plan.planReductionAmount;
            });
            this.registerContext.paymentPlanService.removePaymentPlanReductionSaleLines(this.registerContext, sale);

            if (recalc) {
                return this.registerContext.recalculateSale(false, null, false, sale).then(() => {
                    if (!isReset) {
                        this.registerContext.isRefundMode = false;
                        return this.registerContext.selectSection(RegisterSectionTypeEnum.Services);
                    }
                    return this.registerContext.$q.resolve();
                });
            }
        }

        createPaymentPlanPayment(amount: number = 0): PaymentPlanSalePayment {
            let paymentType = _.findWhere(this.registerContext.paymentTypes, { paymentCategory: Model.PaymentCategoryEnum.PaymentPlan });
            let paymentPlanPayment = new PaymentPlanSalePayment({
                __type: "Millennium.Meevo.Register.Contracts.Model.SalePaymentPaymentPlan, Millennium.Meevo.Register.Contracts.Model",
                paymentTypeId: paymentType.id,
                amount: amount,
                paymentCategory: Model.PaymentCategoryEnum.PaymentPlan,
            });
            return paymentPlanPayment;
        }
        reductionAmountChanged(paymentPlan: IPaymentPlanRefund) {
            if (paymentPlan.planReductionAmount > Math.min(paymentPlan.remainingBalance,paymentPlan.refundAmount)) {
                paymentPlan.planReductionAmount = paymentPlan.previousReductionAmount;
                return;
            }
            paymentPlan.refundCredit = this.registerContext.subtractNormalizedNumbers(paymentPlan.refundAmount, paymentPlan.planReductionAmount);
            if (paymentPlan.refundCredit > paymentPlan.maxInstantRefundAmount) {
                paymentPlan.planReductionAmount = paymentPlan.previousReductionAmount;
                paymentPlan.refundCredit = this.registerContext.subtractNormalizedNumbers(paymentPlan.refundAmount, paymentPlan.planReductionAmount);
                return;
            }
            paymentPlan.previousReductionAmount = paymentPlan.planReductionAmount;
        }

        getTemplate(refresh: boolean = false): angular.IPromise<void> {
            if (this.payment.amount === 0) {
                return this.registerContext.$q.resolve();
            }

            return this.planTemplate && !refresh ? this.registerContext.$q.resolve() :
                this.registerContext.dataService.customMethodData("/api/paymentplan/template", "get", {}, {
                    numberOfPayments: this.numberOfPayments,
                    amount: this.payment.amount
                }).then((response: IPaymentPlanTemplateResponse) => {
                    this.planTemplate = response.data;
                });
        }

        edit(payment: PaymentPlanSalePayment) {
            this.isEditing = true;
            this.registerContext.paymentPlan.hasChanged = false;
            this.payment = jQuery.extend(true, {}, payment);
            this.paymentPlan = jQuery.extend(true, {}, this.registerContext.paymentPlan)
            this.update(this.payment, this.paymentPlan);
        }


        downPaymentAmountChanged() {
            if (this.paymentPlan.downPayment >= this.paymentPlanSaleLineTotal) {
                this.registerContext.modalService.alert("MESSAGE_EXCESSIVE_DOWN_PAYMENT");
                this.paymentPlan.downPayment = this.previousDownPayment;
                return;
            }
            this.update(this.payment, this.paymentPlan, false);
            this.previousDownPayment = this.paymentPlan.downPayment;
            this.update(this.payment, this.paymentPlan);
        }

        numberOfPaymentsChanged() {
            this.update(this.payment, this.paymentPlan);
        }

        addPayment() {
            const paymentPlanPaymentAccount = this.paymentPlan.paymentAccount.id ? this.paymentPlan.paymentAccount.id.toString() :
                undefined;

            var isPaymentPlanChanged = this.paymentPlan.amount != this.payment.amount ||
                this.paymentPlan.clientId != this.paymentPlan.client.id ||
                this.paymentPlan.paymentAccountId != paymentPlanPaymentAccount ||
                this.paymentPlan.downPayment != this.previousDownPayment ||
                this.paymentPlan.numberOfPayments != this.numberOfPayments ||
                this.paymentPlan.firstPaymentDate != this.firstPaymentDate;
            
            if (isPaymentPlanChanged) {
                this.update(this.payment, this.paymentPlan);
                this.paymentPlan.clientId = this.paymentPlan.client.id;
                this.paymentPlan.amount = this.payment.amount;
                this.paymentPlan.paymentAccountId = paymentPlanPaymentAccount;
                this.paymentPlan.numberOfPayments = this.numberOfPayments;
                this.paymentPlan.firstPaymentDate = this.firstPaymentDate;

                this.registerContext.paymentPlan = this.paymentPlan;

                this.payment.paymentPlanSaleLinesSnapshot = JSON.stringify(this.registerContext.sale.saleLines.filter(sl => sl.hasPaymentPlan)
                    .map((sl: Model.SaleLine) => {
                        return this.registerContext.paymentPlanService.getSaleLineSnapshot(sl);
                    }));

                this.registerContext.paymentPlanService.addPaymentPlanPayment(this.registerContext, this.payment).then(() => {
                    this.registerContext.paymentPlanService.sendPaymentPlanForm(this.registerContext).then(() => {
                        super.addPayment(false);
                        this.selectSectionPostAdd();
                    });
                })               
            } else {
                super.addPayment(false);
                this.selectSectionPostAdd();
            }

        }

        public addPaymentDisabled(isValid:boolean) {
            return !isValid || !this.payment || this.payment.amount <= 0 || this.isAddingPayment;
        }

        update(payment: PaymentPlanSalePayment, paymentPlan: Model.PaymentPlan, updateSchedule: boolean = true) {
            if (this.registerContext.isRefundMode) {
                this.buildRefundLines(this.sale);
                return;
            }

            payment.amount = 0;
            this.registerContext.sale.saleLines.forEach(saleLine => {
                if (!saleLine.isRefund && saleLine.hasPaymentPlan) {
                    if (!this.registerContext.paymentPlanService.canBeAddedToPaymentPlan(saleLine)) {
                        saleLine.hasPaymentPlan = false;
                        saleLine.paymentPlanId = null;
                        return;
                    }
                    payment.amount = this.registerContext.addNormalizedNumbers(payment.amount, (saleLine.finalPriceCharge +
                        saleLine.prepaidTip + (saleLine.priceIncludesTax ? 0 : saleLine.tax)));
                }
                this.paymentPlanSaleLineTotal = payment.amount;
            });
            payment.amount = this.registerContext.subtractNormalizedNumbers(payment.amount, this.paymentPlan.downPayment);
            if (payment.amount > this.planTemplate.maximumPaymentPlanAmount) {
                this.paymentPlan.downPayment = this.registerContext.subtractNormalizedNumbers(this.paymentPlanSaleLineTotal, this.planTemplate.maximumPaymentPlanAmount);
                payment.amount = this.planTemplate.maximumPaymentPlanAmount;
                this.minDownPayment = this.paymentPlan.downPayment;
            }
            else {
                this.minDownPayment = 0;
            }
            payment.amount = this.registerContext.roundingService.normalizeDecimalPlaces(payment.amount);
            this.previousDownPayment = this.paymentPlan.downPayment;

            if (updateSchedule) {
                this.getTemplate(true).then(() => {
                    this.updatePaymentSchedule();
                });
            }
        }

        updatePaymentSchedule() {
            if (this.payment.amount === 0) {
                this.paymentSchedule = "";
                return;
            }

            this.paymentSchedule = this.registerContext.$translate.instant("LABEL_PAYMENT_SCHEDULE", {
                numberOfPayments: this.numberOfPayments,
                amountPerPayment: this.meevoCurrency(this.planTemplate.amountPerPayment),
                firstPaymentDate: this.meevoDate(this.paymentPlan.firstPaymentDate)
            });

            if (this.planTemplate.finalPaymentAmount) {
                this.paymentSchedule += this.registerContext.$translate.instant("LABEL_PAYMENT_SCHEDULE_LAST", {
                    finalPaymentAmount: this.meevoCurrency(this.planTemplate.finalPaymentAmount),
                    lastPaymentDate: this.meevoDate(moment(this.paymentPlan.firstPaymentDate).
                        add(this.numberOfPayments - 1, "M").toDate())
                });
            }
        }

        canContinue() {
            return _(this.registerContext.sale.saleLines).any(saleLine =>saleLine.hasPaymentPlan);
        }

        showNote() {
            let options: Meevo.Components.Modal.IModalOptions = {
                templateUrl: "app/plugins/register/modals/noteAdd.template.html",
                controller: "meevo.plugins.register.ModalController",
                controllerAs: "vm",
                bindToController: true,
                locals: {
                    registerContext: this.registerContext,
                    note: this.paymentPlan.note,
                    isChecked: false,
                    modalType: RegisterModalTypeEnum.PaymentPlanNote
                }
            };
            this.registerContext.modalService.showCustom(options);
        }

        public addPaymentLabel() {
            return this.isEditing ? "LABEL_UPDATE_PAYMENT" : "LABEL_ADD_PAYMENT";
        }

        private setHasHsaFsaMerchantAccounts() {
            this.registerContext.getHasHsaFsaMerchantAccounts().then((result) => {
                this.hasHsaFsaMerchantAccount = result;
            });
        }
    }
}